import React from "react";
import {
  Button, Typography, Tabs, Tab, Grid, Box,
  Stack, Paper, Chip, Divider, alpha, useTheme, useMediaQuery,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DownloadIcon from "@mui/icons-material/Download";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { getRole, httpClient, getToken, httpCustomUrlClient } from "../../Service/Service";
import { processUrl } from "Service/product.service";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BASE_URL } from "common/constant";

const getSignedUrl = async (data: any) => {
  try {
    const { fileName, fileType, mainAadhaar, photo_key, tab } = data;
    let response: any;
    if (tab === 0) {
      response = await fetch(`${BASE_URL}customer/uploadimages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileName, fileType, mainAadhaar, photo_key }),
      });
    }
    if (tab === 1) {
      response = await fetch(`${BASE_URL}old/customer/uploadimages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileName, fileType, mainAadhaar, photo_key }),
      });
    }
    if (photo_key === "product") {
      const { originalname } = data;
      response = await fetch(`${BASE_URL}product/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: getToken() || "" },
        body: JSON.stringify({ fileName, fileType, originalname }),
      });
    }
    if (!response.ok) throw new Error("Failed to get signed URL");
    return await response.json();
  } catch (error) {
    return { success: false, message: "Error getting signed URL" };
  }
};

export const uploadFile = async (url: string, file: File): Promise<any> => {
  try {
    return await httpCustomUrlClient(url, "PUT", file);
  } catch (err) {
    console.error(err);
  }
};

// ── Upload Card ──────────────────────────────────────────────
const UploadCard = ({
  label,
  photoKey,
  mainAadhaar,
  value,
  setLoading,
  accentColor,
}: {
  label: string;
  photoKey: string;
  mainAadhaar: string;
  value: number;
  setLoading: (v: boolean) => void;
  accentColor?: string;
}) => {
  const theme = useTheme();
  const accent = accentColor || theme.palette.primary.main;

  const validationSchema = Yup.object({
    file: Yup.mixed()
      .test("fileSize", "File must be < 800 KB", (v: any) => !v || v.size <= 800 * 1024)
      .required("Please select a file"),
  });

  const formik = useFormik({
    initialValues: { file: null as File | null },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const fileName = `photos/customer/${mainAadhaar}/${Date.now()}_jaman_hp_${mainAadhaar}`;
        const signedUrlResult = await getSignedUrl({
          fileName,
          fileType: (values.file as any).type,
          mainAadhaar,
          photo_key: photoKey,
          tab: value,
        });
        if (signedUrlResult.status === "success") {
          await uploadFile(signedUrlResult.data.presignedUrl, values.file as File);
          toast.success(`${label} uploaded successfully!`);
          formik.resetForm();
          const input = document.getElementById(`upload-${photoKey}`) as HTMLInputElement;
          if (input) input.value = "";
        } else {
          toast.error(`Error uploading ${label}.`);
        }
      } catch {
        toast.error(`Error uploading ${label}.`);
      } finally {
        setLoading(false);
      }
    },
  });

  const preview = formik.values.file ? URL.createObjectURL(formik.values.file) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${formik.values.file ? alpha(accent, 0.4) : theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
        transition: "border-color 0.2s",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: alpha(accent, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 16, color: accent }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: accent }}>
          {label}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          {/* Preview */}
          {preview && (
            <Box
              sx={{
                mb: 1.5,
                borderRadius: 1.5,
                overflow: "hidden",
                border: `1px solid ${theme.palette.divider}`,
                maxWidth: 220,
              }}
            >
              <img src={preview} alt={label} style={{ width: "100%", display: "block", maxHeight: 160, objectFit: "cover" }} />
            </Box>
          )}

          {/* File input */}
          <Box
            sx={{
              border: `2px dashed ${formik.values.file ? alpha(accent, 0.5) : theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: alpha(accent, 0.02),
              transition: "all 0.2s",
              "&:hover": { borderColor: accent, bgcolor: alpha(accent, 0.05) },
            }}
            onClick={() => document.getElementById(`upload-${photoKey}`)?.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 28, color: alpha(accent, 0.6), mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              {formik.values.file ? (formik.values.file as File).name : "Click to select image (max 800 KB)"}
            </Typography>
            <input
              type="file"
              id={`upload-${photoKey}`}
              name="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) =>
                formik.setFieldValue("file", e.currentTarget.files?.[0] ?? null)
              }
            />
          </Box>

          {formik.touched.file && formik.errors.file && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {formik.errors.file as string}
            </Typography>
          )}

          {formik.values.file && (
            <Stack direction="row" spacing={1} mt={1.5}>
              <Button
                type="submit"
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  bgcolor: accent,
                  "&:hover": { bgcolor: alpha(accent, 0.85) },
                  boxShadow: `0 3px 10px ${alpha(accent, 0.35)}`,
                }}
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 1.5, fontWeight: 600, textTransform: "none" }}
                onClick={() => {
                  formik.resetForm();
                  const input = document.getElementById(`upload-${photoKey}`) as HTMLInputElement;
                  if (input) input.value = "";
                }}
              >
                Clear
              </Button>
            </Stack>
          )}
        </form>
      </Box>
    </Paper>
  );
};

// ── Doc Display Card ─────────────────────────────────────────
const DocCard = ({ label, url }: { label: string; url?: string }) => {
  const theme = useTheme();
  if (!url) return null;
  const processedUrl = processUrl(url);
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <img
        src={processedUrl}
        alt={label}
        style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }}
      />
      <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="caption" fontWeight={600}>
          {label}
        </Typography>
        <Button
          href={processedUrl}
          target="_blank"
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.7rem" }}
        >
          Download
        </Button>
      </Box>
    </Paper>
  );
};

// ── Main Component ────────────────────────────────────────────
export default function ImageUpload() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [users, setUsers] = React.useState<any[]>([]);
  const [aadhaar, setAadhaar] = React.useState("");
  const [value, setValue] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const handleFind = async (e?: any) => {
    e?.preventDefault();
    if (!aadhaar.trim()) return;
    try {
      setLoading(true);
      const endpoint = value === 0 ? "customer/find" : "old/customer/findone";
      const result = await httpClient(endpoint, "POST", { mainAadhaar: aadhaar });
      if (result.data) {
        setUsers([result.data]);
      } else {
        toast.error("Customer not found.");
        setUsers([]);
      }
    } catch {
      toast.error("Something went wrong!");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const uploadConfigs = [
    { label: "Installation Letter", photoKey: "InstalationLetter", accentColor: theme.palette.primary.main },
    { label: "Satisfaction Letter", photoKey: "satisfactionLetter", accentColor: "#f44336" },
    { label: "Other Letter", photoKey: "otherLetter", accentColor: "#8bc34a" },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3.5 },
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderRadius: 1,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
        }}
      >
        <Box
          sx={{
            bgcolor: alpha("#fff", 0.15),
            borderRadius: 2,
            p: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <CloudUploadIcon sx={{ color: "#fff", fontSize: { xs: 28, sm: 36 } }} />
        </Box>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
            Document Upload
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#fff", 0.8), mt: 0.3 }}>
            Upload installation & satisfaction letters by Aadhaar
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
            overflow: "hidden",
          }}
        >
          {/* Tabs */}
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              px: { xs: 1, sm: 2 },
              pt: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Tabs
              value={value}
              onChange={(_, v) => { setValue(v); setUsers([]); setAadhaar(""); }}
              variant={isMobile ? "fullWidth" : "standard"}
              TabIndicatorProps={{ sx: { height: 3, borderRadius: "3px 3px 0 0", bgcolor: "primary.main" } }}
              sx={{
                minHeight: 52,
                "& .MuiTab-root": {
                  minHeight: 52,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  color: "text.secondary",
                  gap: 0.75,
                  "&.Mui-selected": { color: "primary.main" },
                },
              }}
            >
              <Tab label="New Customer" icon={<FiberNewIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Old Customer" icon={<HistoryIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
          </Box>

          <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 3, pb: 3 }}>
            {/* Search */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.7rem" }}
            >
              Find Customer by Aadhaar
            </Typography>
            <Paper
              elevation={0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: { xs: 1.5, sm: 2 }, mb: 3 }}
            >
              <form onSubmit={handleFind}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Main Aadhaar No"
                    type="number"
                    value={aadhaar}
                    onChange={(e) => setAadhaar(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: <FingerprintIcon sx={{ fontSize: 16, color: "text.disabled", mr: 0.5 }} />,
                    }}
                    sx={{ maxWidth: { sm: 340 } }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFind}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: "none",
                      px: 3,
                      height: 40,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                    }}
                  >
                    {loading ? "Searching..." : "Find & Upload"}
                  </Button>
                </Stack>
              </form>
            </Paper>

            {/* Results */}
            {users.map((user, i) => (
              <Box key={i}>
                {/* Customer info card */}
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonOutlineIcon sx={{ fontSize: 16, color: "primary.main" }} />
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                        {user.name?.toUpperCase()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {user.installtatus === "Complete" && (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                          label="Complete"
                          size="small"
                          color="success"
                          sx={{ fontWeight: 700, fontSize: "0.65rem" }}
                        />
                      )}
                      {!user.consumerNo && (
                        <Chip
                          label="No Consumer No."
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: "0.65rem" }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Grid container spacing={1.5}>
                      {[
                        ["Main Aadhaar", user.mainAadhaar],
                        ["Family Aadhaar", user.familyAadhaar],
                        ["Mobile", user.mobile],
                        ["Reg. No", user.regNo || "NA"],
                        ["Consumer No", user.consumerNo || "NA"],
                        ["Main Agent", user.mainAgent?.toUpperCase()],
                        ["Agency", user.registeredAgencyName || "NA"],
                        ["Added By", user.addedBy || "NA"],
                      ].map(([label, val]) => (
                        <Grid item xs={6} sm={4} md={3} key={label}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                            {label}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {val}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>

                {/* Upload sections */}
                {user.installtatus && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 2, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.7rem" }}
                    >
                      Upload Documents
                    </Typography>
                    <Grid container spacing={2} mb={3}>
                      {uploadConfigs.map((cfg) => {
                        if (cfg.photoKey === "InstalationLetter") {
                          if (
                            !(getRole() === "superadmin" || (getRole() === "admin" && user.installtatus !== "Complete"))
                          ) {
                            if (user.installtatus === "Complete") {
                              return (
                                <Grid item xs={12} sm={6} md={4} key={cfg.photoKey}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      border: `1px solid ${theme.palette.divider}`,
                                      borderRadius: 2,
                                      p: 2,
                                      bgcolor: alpha(theme.palette.success.main, 0.04),
                                    }}
                                  >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} />
                                      <Typography variant="body2" color="success.dark" fontWeight={600}>
                                        Installation photo already submitted
                                      </Typography>
                                    </Stack>
                                  </Paper>
                                </Grid>
                              );
                            }
                            return null;
                          }
                        }
                        return (
                          <Grid item xs={12} sm={6} md={4} key={cfg.photoKey}>
                            <UploadCard
                              label={cfg.label}
                              photoKey={cfg.photoKey}
                              mainAadhaar={user.mainAadhaar}
                              value={value}
                              setLoading={setLoading}
                              accentColor={cfg.accentColor}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {/* Existing documents */}
                {(user.InstalationLetter || user.satisfactionLetter || user.swasthyaSathiLetter || user.otherLetter) && (
                  <Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 2, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.7rem" }}
                    >
                      Existing Documents
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <DocCard label="Installation Letter" url={user.InstalationLetter} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DocCard label="Satisfaction Letter" url={user.satisfactionLetter} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DocCard label="Swasthya Sathi" url={user.swasthyaSathiLetter} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DocCard label="Other Letter" url={user.otherLetter} />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
